import { Navigate, Route, Routes } from 'react-router';
import { Brand } from './components';
import { Link } from 'react-router';
import { ProtectedRoute } from './auth';
import { lazy, Suspense, useEffect, useState } from 'react';
import { api } from './api';
import { useI18n } from './i18n';

const LandingPage = lazy(() => import('./pages/Landing').then((module) => ({ default: module.LandingPage })));
const LoginPage = lazy(() => import('./pages/Auth').then((module) => ({ default: module.LoginPage })));
const MfaSetupPage = lazy(() => import('./pages/Auth').then((module) => ({ default: module.MfaSetupPage })));
const InvitationPage = lazy(() => import('./pages/Auth').then((module) => ({ default: module.InvitationPage })));
const ClientDashboard = lazy(() => import('./pages/Client').then((module) => ({ default: module.ClientDashboard })));
const ClientRequestsPage = lazy(() => import('./pages/Client').then((module) => ({ default: module.ClientRequestsPage })));
const NewRequestPage = lazy(() => import('./pages/Client').then((module) => ({ default: module.NewRequestPage })));
const RequestDetailPage = lazy(() => import('./pages/Client').then((module) => ({ default: module.RequestDetailPage })));
const ClientNotificationsPage = lazy(() => import('./pages/Client').then((module) => ({ default: module.ClientNotificationsPage })));
const AdminDashboard = lazy(() => import('./pages/Admin').then((module) => ({ default: module.AdminDashboard })));
const AdminRequestsPage = lazy(() => import('./pages/Admin').then((module) => ({ default: module.AdminRequestsPage })));
const AdminRequestDetail = lazy(() => import('./pages/Admin').then((module) => ({ default: module.AdminRequestDetail })));
const ClientsPage = lazy(() => import('./pages/Admin').then((module) => ({ default: module.ClientsPage })));
const TeamPage = lazy(() => import('./pages/Admin').then((module) => ({ default: module.TeamPage })));
const RolesPage = lazy(() => import('./pages/Admin').then((module) => ({ default: module.RolesPage })));
const ApprovalsPage = lazy(() => import('./pages/Admin').then((module) => ({ default: module.ApprovalsPage })));
const SettingsPage = lazy(() => import('./pages/Admin').then((module) => ({ default: module.SettingsPage })));
const ProfilePage = lazy(() => import('./pages/Account').then((module) => ({ default: module.ProfilePage })));
const SecurityPage = lazy(() => import('./pages/Account').then((module) => ({ default: module.SecurityPage })));
const SupportPage = lazy(() => import('./pages/Account').then((module) => ({ default: module.SupportPage })));
const AltLandingPage = lazy(() => import('./pages/AltLanding').then((module) => ({ default: module.AltLandingPage })));
const Alt2LandingPage = lazy(() => import('./pages/Alt2Landing').then((module) => ({ default: module.Alt2LandingPage })));
const Alt3LandingPage = lazy(() => import('./pages/Alt3Landing').then((module) => ({ default: module.Alt3LandingPage })));
const LandingTest1 = lazy(() => import('./pages/LandingTests').then((module) => ({ default: module.LandingTest1 })));
const LandingTest2 = lazy(() => import('./pages/LandingTests').then((module) => ({ default: module.LandingTest2 })));
const LandingTest3 = lazy(() => import('./pages/LandingTests').then((module) => ({ default: module.LandingTest3 })));
const LandingTest4 = lazy(() => import('./pages/LandingTests').then((module) => ({ default: module.LandingTest4 })));
const LandingTest5 = lazy(() => import('./pages/LandingTests').then((module) => ({ default: module.LandingTest5 })));

function LegalPage({ kind }: { kind: 'privacy' | 'terms' }) {
	const { locale } = useI18n();
	const text = legalCopy[locale];
	const [config, setConfig] = useState<{
		supportEmail?: string | null;
		supportPhone?: string | null;
		supportMessenger?: string | null;
		legalReady?: boolean;
	}>({});
	useEffect(() => {
		void api<typeof config>('/api/v1/public/config')
			.then(setConfig)
			.catch(() => undefined);
	}, []);
	useEffect(() => {
		document.title = `${kind === 'privacy' ? text.privacyTitle : text.termsTitle} — Volt`;
	}, [kind, text]);
	const sections = kind === 'privacy' ? text.privacySections : text.termsSections;
	return (
		<div className="legal-page">
			<header>
				<Link to="/">
					<Brand />
				</Link>
				<Link to="/">{text.back}</Link>
			</header>
			<main>
				<span className="eyebrow">{text.eyebrow}</span>
				<h1>{kind === 'privacy' ? text.privacyTitle : text.termsTitle}</h1>
				{!config.legalReady && <p className="legal-lead">{text.launchNotice}</p>}
				{sections.map(([title, body]) => (
					<section key={title}>
						<h2>{title}</h2>
						<p>{body}</p>
					</section>
				))}
				{(config.supportEmail || config.supportPhone || config.supportMessenger) && (
					<section>
						<h2>{text.contacts}</h2>
						<div className="legal-contacts">
							{config.supportEmail && <a href={`mailto:${config.supportEmail}`}>{config.supportEmail}</a>}
							{config.supportPhone && <a href={`tel:${config.supportPhone}`}>{config.supportPhone}</a>}
							{config.supportMessenger && (
								<a href={config.supportMessenger} target="_blank" rel="noreferrer">
									{text.messenger}
								</a>
							)}
						</div>
					</section>
				)}
			</main>
		</div>
	);
}

const legalCopy = {
	ru: {
		back: 'На главную',
		eyebrow: 'Информация о сервисе',
		privacyTitle: 'Конфиденциальность и данные',
		termsTitle: 'Условия использования',
		launchNotice:
			'Самостоятельная регистрация пока закрыта. Полные юридические реквизиты и утверждённая версия документов будут опубликованы до открытия доступа.',
		contacts: 'Контакты',
		messenger: 'Открыть мессенджер',
		privacySections: [
			[
				'Какие данные обрабатываются',
				'Контактные данные аккаунта, содержание заявок, загруженные файлы, история офферов, платёжные снимки и технические журналы безопасности.',
			],
			[
				'Для чего используются данные',
				'Для поиска товара, подготовки предложений, сопровождения оплаты, закупки и доставки, а также для защиты аккаунта и выполнения обязательств перед клиентом.',
			],
			[
				'Хранение и доступ',
				'Доступ получают владелец заявки и сотрудники с соответствующими ролевыми правами. Внутренние заметки команды не передаются клиентскому интерфейсу.',
			],
		],
		termsSections: [
			[
				'Назначение сервиса',
				'Сервис фиксирует запросы на поиск и закупку товара, коммерческие предложения, платёжные инструкции и этапы выполнения. Оплата проходит вне сайта.',
			],
			[
				'Коммерческие предложения',
				'Каждая опубликованная версия оффера имеет срок действия. После выбора клиентом изменения оформляются новой версией.',
			],
			['Платежи', 'Реквизиты, курс и сумма фиксируются в платёжном снимке. Загрузка квитанции клиентом не означает подтверждение оплаты.'],
		],
	},
	ua: {
		back: 'На головну',
		eyebrow: 'Інформація про сервіс',
		privacyTitle: 'Конфіденційність і дані',
		termsTitle: 'Умови використання',
		launchNotice:
			'Самостійну реєстрацію поки закрито. Повні юридичні реквізити та затверджену версію документів буде опубліковано до відкриття доступу.',
		contacts: 'Контакти',
		messenger: 'Відкрити месенджер',
		privacySections: [
			[
				'Які дані обробляються',
				'Контактні дані акаунта, зміст заявок, завантажені файли, історія оферів, платіжні знімки й технічні журнали безпеки.',
			],
			[
				'Для чого використовуються дані',
				'Для пошуку товару, підготовки пропозицій, супроводу оплати, закупівлі й доставки, а також для захисту акаунта та виконання зобов’язань перед клієнтом.',
			],
			[
				'Зберігання та доступ',
				'Доступ мають власник заявки та співробітники з відповідними рольовими правами. Внутрішні нотатки команди не передаються до клієнтського інтерфейсу.',
			],
		],
		termsSections: [
			[
				'Призначення сервісу',
				'Сервіс фіксує запити на пошук і закупівлю товару, комерційні пропозиції, платіжні інструкції та етапи виконання. Оплата відбувається поза сайтом.',
			],
			['Комерційні пропозиції', 'Кожна опублікована версія офера має строк дії. Після вибору клієнтом зміни оформлюються новою версією.'],
			[
				'Платежі',
				'Реквізити, курс і сума фіксуються у платіжному знімку. Завантаження квитанції клієнтом не означає підтвердження оплати.',
			],
		],
	},
	en: {
		back: 'Back home',
		eyebrow: 'Service information',
		privacyTitle: 'Privacy and data',
		termsTitle: 'Terms of use',
		launchNotice:
			'Self-registration is not open yet. Full legal entity details and approved legal documents will be published before access opens.',
		contacts: 'Contact',
		messenger: 'Open messenger',
		privacySections: [
			[
				'Data we process',
				'Account contact details, request content, uploaded files, offer history, payment snapshots, and technical security logs.',
			],
			[
				'How data is used',
				'To source products, prepare offers, support payments, purchasing, and delivery, and to protect accounts and fulfil our obligations to clients.',
			],
			[
				'Storage and access',
				'Access is limited to the request owner and staff with the appropriate role permissions. Internal team notes are never returned to the client interface.',
			],
		],
		termsSections: [
			[
				'Purpose of the service',
				'The service records product sourcing requests, offers, payment instructions, and fulfilment stages. Payments take place outside the website.',
			],
			['Offers', 'Every published offer version has an expiry time. Changes after client selection are issued as a new version.'],
			[
				'Payments',
				'Payment details, exchange rate, and amount are fixed in a payment snapshot. Uploading evidence does not confirm payment until an authorised employee verifies it.',
			],
		],
	},
} as const;

export default function App() {
	return (
		<Suspense
			fallback={
				<div className="app-loading" role="status">
					<span />
					<p>Загружаем Вольт…</p>
				</div>
			}
		>
			<Routes>
				<Route path="/" element={<LandingPage />} />
				<Route path="/alt" element={<AltLandingPage />} />
				<Route path="/alt2" element={<Alt2LandingPage />} />
				<Route path="/alt3" element={<Alt3LandingPage />} />
				<Route path="/login" element={<LoginPage />} />
				<Route path="/invite/:token" element={<InvitationPage />} />
				<Route
					path="/security/setup"
					element={
						<ProtectedRoute allowAny>
							<MfaSetupPage />
						</ProtectedRoute>
					}
				/>
				<Route path="/privacy" element={<LegalPage kind="privacy" />} />
				<Route path="/terms" element={<LegalPage kind="terms" />} />
				<Route path="/test1" element={<LandingTest1 />} />
				<Route path="/test2" element={<LandingTest2 />} />
				<Route path="/test3" element={<LandingTest3 />} />
				<Route path="/test4" element={<LandingTest4 />} />
				<Route path="/test5" element={<LandingTest5 />} />
				<Route
					path="/app"
					element={
						<ProtectedRoute>
							<ClientDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/app/requests"
					element={
						<ProtectedRoute>
							<ClientRequestsPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/app/requests/new"
					element={
						<ProtectedRoute>
							<NewRequestPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/app/requests/:id"
					element={
						<ProtectedRoute>
							<RequestDetailPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/app/notifications"
					element={
						<ProtectedRoute>
							<ClientNotificationsPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/app/profile"
					element={
						<ProtectedRoute>
							<ProfilePage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/app/security"
					element={
						<ProtectedRoute>
							<SecurityPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/app/support"
					element={
						<ProtectedRoute>
							<SupportPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/admin"
					element={
						<ProtectedRoute staff>
							<AdminDashboard />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/admin/requests"
					element={
						<ProtectedRoute staff>
							<AdminRequestsPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/admin/requests/:id"
					element={
						<ProtectedRoute staff>
							<AdminRequestDetail />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/admin/clients"
					element={
						<ProtectedRoute staff>
							<ClientsPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/admin/team"
					element={
						<ProtectedRoute staff>
							<TeamPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/admin/roles"
					element={
						<ProtectedRoute staff>
							<RolesPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/admin/approvals"
					element={
						<ProtectedRoute staff>
							<ApprovalsPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/admin/settings"
					element={
						<ProtectedRoute staff>
							<SettingsPage />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/admin/profile"
					element={
						<ProtectedRoute staff>
							<ProfilePage admin />
						</ProtectedRoute>
					}
				/>
				<Route
					path="/admin/security"
					element={
						<ProtectedRoute staff>
							<SecurityPage admin />
						</ProtectedRoute>
					}
				/>
				<Route path="*" element={<Navigate to="/" replace />} />
			</Routes>
		</Suspense>
	);
}
